import React, { useState } from 'react';
import { Plus, X, Check, Copy, Download, Upload } from 'lucide-react';
import trainingApi from '../api';
import { LANGUAGES, labelFor } from '../i18n/strings';

const AI_PROMPT = `Generate quiz questions from the attached slides. Reply with ONLY valid JSON in this exact shape (no prose, no markdown fences):

[
  {
    "question_text": "...",
    "options": [
      { "option_text": "...", "is_correct": false },
      { "option_text": "...", "is_correct": true }
    ]
  }
]

Rules:
- 2 to 6 options per question
- exactly one option must have "is_correct": true
- aim for ~10 questions unless I tell you otherwise
- questions should be derived strictly from the slide content`;

const blankQuestion = () => ({
  id: null,
  question_text: '',
  question_order: 0,
  options: [
    { option_text: '', is_correct: false },
    { option_text: '', is_correct: false },
  ],
});

const toExportShape = (questions) =>
  questions.map((q) => ({
    question_text: q.question_text,
    options: q.options.map((o) => ({
      option_text: o.option_text,
      is_correct: !!o.is_correct,
    })),
  }));

const validateImport = (parsed) => {
  if (!Array.isArray(parsed)) return 'Top-level JSON must be an array of questions.';
  if (parsed.length === 0) return 'At least one question is required.';
  for (let i = 0; i < parsed.length; i++) {
    const q = parsed[i];
    const label = `Question ${i + 1}`;
    if (!q || typeof q !== 'object') return `${label}: must be an object.`;
    if (typeof q.question_text !== 'string' || !q.question_text.trim()) return `${label}: question_text is required.`;
    if (!Array.isArray(q.options) || q.options.length < 2 || q.options.length > 6) {
      return `${label}: options must be 2 to 6 entries.`;
    }
    let correct = 0;
    for (let j = 0; j < q.options.length; j++) {
      const o = q.options[j];
      if (!o || typeof o !== 'object') return `${label}, option ${j + 1}: must be an object.`;
      if (typeof o.option_text !== 'string' || !o.option_text.trim()) return `${label}, option ${j + 1}: option_text is required.`;
      if (typeof o.is_correct !== 'boolean') return `${label}, option ${j + 1}: is_correct must be true or false.`;
      if (o.is_correct) correct += 1;
    }
    if (correct !== 1) return `${label}: must have exactly one option marked correct (got ${correct}).`;
  }
  return null;
};

const QuestionEditor = ({ trainingId, questions, onChange }) => {
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [copyMsg, setCopyMsg] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const flashCopy = (msg) => {
    setCopyMsg(msg);
    setTimeout(() => setCopyMsg(''), 2000);
  };

  const startNew = () => setEditing({ ...blankQuestion(), question_order: questions.length });
  const startEdit = (q) => setEditing(JSON.parse(JSON.stringify(q)));
  const cancel = () => { setEditing(null); setError(''); };

  const setOption = (i, patch) => {
    setEditing((cur) => ({
      ...cur,
      options: cur.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
    }));
  };
  const addOption = () => setEditing((cur) => ({ ...cur, options: [...cur.options, { option_text: '', is_correct: false }] }));
  const removeOption = (i) => setEditing((cur) => ({ ...cur, options: cur.options.filter((_, idx) => idx !== i) }));
  const setSingleCorrect = (i) => setEditing((cur) => ({
    ...cur,
    options: cur.options.map((o, idx) => ({ ...o, is_correct: idx === i })),
  }));

  const save = async () => {
    setError('');
    if (!editing.question_text.trim()) return setError('Question text required.');
    if (editing.options.length < 2) return setError('At least 2 options required.');
    if (editing.options.some((o) => !o.option_text.trim())) return setError('All options must have text.');
    if (!editing.options.some((o) => o.is_correct)) return setError('Mark one option as correct.');

    try {
      await trainingApi.adminUpsertQuestion(trainingId, {
        question_id: editing.id,
        question_text: editing.question_text,
        question_order: editing.question_order,
        options: editing.options.map((o) => ({
          option_text: o.option_text,
          is_correct: !!o.is_correct,
        })),
      });
      setEditing(null);
      onChange();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteQuestion = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await trainingApi.adminDeleteQuestion(qid);
      onChange();
    } catch (e) {
      setError(e.message);
    }
  };

  const copyAiPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT);
      flashCopy('AI prompt copied');
    } catch (e) {
      setError('Clipboard not available. Copy the prompt from the docs.');
    }
  };

  const exportQuestions = async () => {
    if (questions.length === 0) {
      setError('Nothing to export — no questions yet.');
      return;
    }
    const json = JSON.stringify(toExportShape(questions), null, 2);
    try {
      await navigator.clipboard.writeText(json);
      flashCopy('Exported to clipboard + downloaded');
    } catch (e) {
      flashCopy('Downloaded (clipboard unavailable)');
    }
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-${trainingId}-questions.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold">Quiz Questions ({questions.length})</h3>
        <div className="flex flex-wrap items-center gap-2">
          {copyMsg && (
            <span className="text-xs text-emerald-300 mr-1">{copyMsg}</span>
          )}
          <button
            type="button"
            onClick={copyAiPrompt}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 flex items-center gap-1 text-sm"
            title="Copy an AI prompt + JSON schema to clipboard"
          >
            <Copy className="w-4 h-4" /> Copy AI Prompt
          </button>
          <button
            type="button"
            onClick={exportQuestions}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 flex items-center gap-1 text-sm"
            title="Copy current questions to clipboard and download as .json"
          >
            <Download className="w-4 h-4" /> Export
          </button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 flex items-center gap-1 text-sm"
            title="Import questions from JSON (replaces existing)"
          >
            <Upload className="w-4 h-4" /> Import
          </button>
          <button
            type="button"
            onClick={startNew}
            className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-1 text-sm"
          >
            <Plus className="w-4 h-4" /> Add Question
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm">{error}</div>
      )}

      <ul className="space-y-2 mb-4">
        {questions.map((q, i) => (
          <li
            key={q.id}
            className="p-3 rounded bg-slate-900 border border-slate-700 flex items-center justify-between"
          >
            <div>
              <div className="text-sm font-medium">{i + 1}. {q.question_text}</div>
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <span>{q.options.length} options</span>
                {/* Which languages already have this question translated. Editing the
                    base text here does not update these — the translation tabs do. */}
                {LANGUAGES.filter((l) => (q.translations?.[l.code] || '').trim()).map((l) => (
                  <span
                    key={l.code}
                    className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300"
                    title={`${labelFor(l.code)} translation exists`}
                  >
                    {l.code}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => startEdit(q)}
                className="px-2 py-1 text-amber-300 hover:text-amber-200 text-sm"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => deleteQuestion(q.id)}
                className="px-2 py-1 text-rose-300 hover:text-rose-200 text-sm"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
        {questions.length === 0 && (
          <li className="text-sm text-slate-400">No questions yet.</li>
        )}
      </ul>

      {editing && (
        <div className="rounded-lg bg-slate-900 border border-amber-700 p-4 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Question</label>
            <textarea
              rows={2}
              value={editing.question_text}
              onChange={(e) => setEditing({ ...editing, question_text: e.target.value })}
              className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100"
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">Options (mark exactly one correct)</div>
            {editing.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setSingleCorrect(i)}
                  className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                    opt.is_correct ? 'border-emerald-500 bg-emerald-500/20' : 'border-slate-600'
                  }`}
                  title="Mark as correct"
                >
                  {opt.is_correct && <Check className="w-4 h-4 text-emerald-400" />}
                </button>
                <input
                  type="text"
                  value={opt.option_text}
                  onChange={(e) => setOption(i, { option_text: e.target.value })}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100"
                />
                {editing.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className="p-1 text-slate-400 hover:text-rose-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addOption}
              className="text-sm text-amber-300 hover:text-amber-200"
            >
              + Add another option
            </button>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600">
              Cancel
            </button>
            <button type="button" onClick={save} className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white">
              {editing.id ? 'Save Changes' : 'Add Question'}
            </button>
          </div>
        </div>
      )}

      {importOpen && (
        <ImportQuestionsModal
          trainingId={trainingId}
          existingCount={questions.length}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); onChange(); }}
        />
      )}
    </div>
  );
};

const ImportQuestionsModal = ({ trainingId, existingCount, onClose, onImported }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setText(String(e.target.result || ''));
    reader.onerror = () => setError('Failed to read file.');
    reader.readAsText(file);
  };

  const submit = async () => {
    setError('');
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      return;
    }
    const msg = validateImport(parsed);
    if (msg) { setError(msg); return; }
    setSubmitting(true);
    try {
      await trainingApi.adminImportQuestions(trainingId, parsed);
      onImported();
    } catch (e) {
      // Import replaces every question, which also destroys the Spanish/Hmong text
      // attached to the old ones. The server refuses the first time and reports how
      // much would be lost so this can be an informed decision.
      if (e.status === 409 && e.body?.requires_confirmation) {
        const proceed = window.confirm(
          `${e.body.translation_count} translation(s) for the other languages are attached to the current questions.\n\n`
          + 'Replacing the questions deletes them, and every language will show the new English text until it is translated again.\n\n'
          + 'Continue?'
        );
        if (!proceed) { setSubmitting(false); return; }
        try {
          await trainingApi.adminImportQuestions(trainingId, parsed, true);
          onImported();
        } catch (e2) {
          setError(e2.message);
        } finally {
          setSubmitting(false);
        }
        return;
      }
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-100">Import Questions from JSON</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="p-3 rounded bg-amber-900/30 border border-amber-700 text-amber-200 text-sm">
            <strong>This will REPLACE all existing questions for this training</strong>
            {existingCount > 0 && ` (${existingCount} currently).`}
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Paste JSON</label>
            <textarea
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='[{"question_text": "...", "options": [{"option_text":"...","is_correct":true}, ...]}]'
              className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 font-mono text-xs"
            />
          </div>

          <div>
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm cursor-pointer">
              <Upload className="w-4 h-4" />
              Choose .json file
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          </div>

          {error && (
            <div className="p-2 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm">{error}</div>
          )}
        </div>

        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !text.trim()}
            className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Importing…' : 'Replace & Import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditor;
